<template>
  <div
    class="max-w-full flex gap-1"
    :class="{
      'flex-1': fullWidth,
      'flex-row': flexDirection === 'horizontal',
      'flex-col': flexDirection === 'vertical',
      'flex-wrap': !useMinimumHeight,
    }"
    :title="
      activateTooltip
        ? null
        : (entitiesToDisplay ?? []).map((i) => i.designation).join(', ')
    "
  >
    <div
      v-for="item in entitiesToDisplay ?? []"
      :key="item.designation"
      class="max-w-full font-medium items-center overflow-x-hidden relative"
      :class="{
        'w-full': fullWidth,
      }"
      ref="draggableItems"
      @click.stop="handleClickIfEmptyDesignation(item)"
    >
      <div
        class="max-w-full flex flex-row bg-panel entity-list-item"
        :class="{
          'h-6 rounded-full overflow-hidden': useMinimumHeight,
          'min-h-6 rounded-lg': !useMinimumHeight,
        }"
        :style="generateCssBorderStyle(item)"
      >
        <div
          class="text-xs font-normal leading-none max-w-full leading-snug flex-1 flex py-1"
          :class="{
            'flex-row items-center justify-items': centerContent,
            'items-start flex-col': !centerContent,
            'pl-2.5': useMinimumHeight,
            'pl-2 rounded-l-md': !useMinimumHeight,
          }"
          :style="generateCssStyle(item)"
          @click.stop="clickWholeItem ? handleClick(item) : null"
          @mouseenter="($event) => displayTooltip($event, item)"
          @mouseleave="hideTooltip"
        >
          <slot name="prefix-icon" :item="item">
            <div class="cursor-grab" @mousedown="$emit('startGrab', item)">
              <hit-icon v-if="draggable" icon="drag" size="xs" />
            </div>
          </slot>
          <div
            class="max-w-full grid grid-flow-col overflow-hidden"
            :class="{
              'px-1': !spacyLayout,
              'p-0.5 text-xs font-normal leading-none ': spacyLayout,
            }"
          >
            <span
              :class="{
                'hover:underline cursor-pointer': redirectRouteDataFn,
                'whitespace-pre-wrap overflow-hidden break-words':
                  !useMinimumHeight,
                'leading-normal': spacyLayout,
              }"
              @click.stop="handleClick(item)"
              @mousedown.middle.stop="handleMouseDownMiddle(item)"
            >
              {{ item.designation }}
            </span>
          </div>
          <div>
            <slot name="additionalLine" :item="item" />
          </div>
        </div>
        <div
          class="flex flex-col items-center py-1"
          :class="{
            'px-1.5': useMinimumHeight,
            'px-1 rounded-r-md': !useMinimumHeight,
          }"
          :style="generateCssStyleIcons(item)"
        >
          <hit-icon
            v-if="clearable && !item?.disableActions"
            icon="clear"
            clickable
            @click="
              $emit('remove', item.id);
              hideTooltip();
            "
          />
          <hit-icon
            v-for="(icon, index) in item?.disableActions ? [] : customIcons"
            :key="index"
            :icon="icon.icon"
            :size="customIconSize"
            clickable
            @click="
              $emit(icon.event, item);
              hideTooltip();
            "
          />
          <hit-dropdown
            v-if="
              !item?.disableActions &&
              dropdownEventsFn &&
              dropdownEventsFn(item).length > 0
            "
            icon="option-menu"
            :custom-bg-color="generateCssStyleIcons(item)['background-color']"
            :icon-color-style="{color: 'var(--text-accent)'}"
            icon-size="sm"
            remove-padding
            remove-border
            remove-margin
            :items="dropdownItems(item)"
          />
        </div>
      </div>
    </div>

    <Teleport v-if="tooltipItem" to="#root-element">
      <div
        v-if="tooltipItem"
        class="absolute bg-header text-header text-xs rounded-lg p-2 max-w-64"
        :style="tooltipPositionStyle"
      >
        <slot name="tooltip" :item="tooltipItem">
          {{ tooltipItem?.designation }}
        </slot>
      </div>
    </Teleport>
  </div>
</template>

<script>
import {HitIcon} from '../icon';
import {ColourUtils} from '../../utils/colour';
import {HitDropdown, DropdownItem} from '../dropdown';

export default {
  name: 'HitEntityList',
  components: {
    HitIcon,
    HitDropdown,
  },
  props: {
    /**
     * Array with the different entities to display. Each entity has an object with:
     *    - id: id used to identify the record
     *    - designation: designation that will be displayed
     */
    entitiesToDisplay: {
      type: Array,
      required: false,
      default: () => [],
    },

    /**
     * Boolean to activate the display of the cancel icon behind each record
     */
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Array of custom icons that are displayed at the end of the entity list
     */
    customIcons: {
      type: Array,
      required: false,
      default: null,
    },

    /**
     * Array of dropdown events that are displayed at the end of the entity list
     */
    dropdownEventsFn: {
      type: Function,
      required: false,
      default: null,
    },

    /**
     * If we defined a function to add dropdown events to each entity, we need to define all the possible emits
     */
    dropdwonEmits: {
      type: Array,
      required: false,
      default: () => [],
    },

    /**
     * Use a specific icon size for the custom icon
     */
    customIconSize: {
      type: String,
      required: false,
      default: 'xs',
    },

    /**
     * We need to pass a function that returns the name of the detail page we
     * want to redirect to because we can have entities of different types in the
     * same list
     */
    redirectRouteDataFn: {
      type: Function,
      required: false,
      default: null,
    },

    /**
     * Flag to make the entity list full width of the parent element
     */
    fullWidth: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * In which direction the list should be flexed
     */
    flexDirection: {
      type: String,
      required: false,
      default: 'horizontal',
      validator: (value) => {
        return ['horizontal', 'vertical'].indexOf(value) !== -1;
      },
    },

    /**
     * When used with more text, we can activate this layout to increase the readibility
     */
    spacyLayout: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * If set to true, aligns the content vertically in the middle, otherwise: items-start
     */
    centerContent: {
      type: Boolean,
      required: false,
      default: true,
    },

    /**
     * Displays the entities on one line with no wrapping
     */
    useMinimumHeight: {
      type: Boolean,
      required: false,
      default: true,
    },

    /**
     * Creates a contrast between the content and the icon bar
     */
    applyContrast: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Adapts the styling to make each record draggable
     */
    draggable: {
      type: Boolean,
      required: false,
      default: false,
    },

    activateTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },
    clickWholeItem: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      tooltipItem: null,
      isGrabbing: false,
      mouseX: 0,
      mouseY: 0,
    };
  },
  computed: {
    screenHeight() {
      return window.innerHeight;
    },
    screenWidth() {
      return window.innerWidth;
    },

    emitsList() {
      let emits = this.customIcons
        .map((icon) => icon.event)
        .concat(this.dropdwonEmits)
        .concat(['remove', 'startGrab', 'item-clicked']);
      return emits;
    },

    tooltipPositionStyle() {
      const offset = 15;
      const horizontal =
        this.mouseX > this.screenWidth / 2
          ? {right: `${this.screenWidth - (this.mouseX - offset)}px`}
          : {left: `${this.mouseX + offset}px`};
      const vertical =
        this.mouseY > this.screenHeight / 2
          ? {bottom: `${this.screenHeight - (this.mouseY - offset)}px`}
          : {top: `${this.mouseY + offset}px`};
      return {
        position: 'absolute',
        ...horizontal,
        ...vertical,
      };
    },
  },
  methods: {
    dropdownItems(item) {
      return this.dropdownEventsFn(item).map(
        (event) =>
          new DropdownItem({
            label: event.label,
            icon: event.icon,
            clickEventHandler: () => this.$emit(event.event, item),
          }),
      );
    },
    displayTooltip(event, item) {
      if (!this.activateTooltip) return;
      this.mouseX = event.clientX;
      this.mouseY = event.clientY;
      this.tooltipItem = item;
    },

    hideTooltip() {
      this.tooltipItem = null;
    },

    handleClick(item) {
      this.$emit('item-clicked', item);
      this.redirectTo(item);
    },

    /**
     * If the item has no designation, we also redirect if only clicking into the box
     */
    handleClickIfEmptyDesignation(item) {
      if (item?.designation) return;
      this.$emit('item-clicked', item);
      this.redirectTo(item);
    },

    /**
     * Calls the redirect function if it has been passed to the component
     */
    redirectTo(item) {
      if (this.redirectRouteDataFn) {
        const routeData = this.redirectRouteDataFn(item);
        this.$router.push(routeData);
      }
    },

    handleMouseDownMiddle(item) {
      if (this.redirectRouteDataFn) {
        const routeData = this.redirectRouteDataFn(item);
        const a = document.createElement('a');
        a.href = routeData.fullPath;
        a.target = '_blank';
        const e = new MouseEvent('click', {
          ctrlKey: true,
          metaKey: true,
        });
        a.dispatchEvent(e);
      }
    },

    generateCssStyleIcons(tag) {
      return {
        'background-color': tag?.colour ? `#${tag.colour}` : 'var(--color-tag)',
        opacity: 1,
        color: `${
          tag.colour
            ? ColourUtils.getFontColorOnBg(tag.colour)
            : 'var(--text-tag)'
        } !important`,
      };
    },

    generateCssStyle(tag) {
      let opacity = 1;
      if (this.applyContrast) opacity = 0.8;
      return {
        'background-color': tag?.colour ? `#${tag.colour}` : 'var(--color-tag)',
        opacity: opacity,
        color: `${
          tag.colour
            ? ColourUtils.getFontColorOnBg(tag.colour, opacity)
            : 'var(--text-tag)'
        } !important`,
      };
    },

    generateCssBorderStyle(tag) {
      if (!tag?.borderColour) return {};
      return {
        border: `2px solid #${tag.borderColour}`,
        'padding-top': '-1px',
        'padding-bottom': '-1px',
      };
    },
  },
};
</script>

<style scoped>
.tooltip {
  transition:
    top 0.1s ease,
    left 0.1s ease;
}
</style>
